/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useTranslation } from 'react-i18next';

import { Namespaces } from '../../../../i18n';
import { useDetailsPanelInfoStyles } from '../../../../styles/detailsPanelInfo';
import { AdminOrganization } from '../../../../types/superuser/AdminOrganization';

type AdminOrganizationDetailsPanelProps = {
  rowData: AdminOrganization;
};

export function AdminOrganizationDetailsPanel(props: AdminOrganizationDetailsPanelProps) {
  const { rowData } = props;
  const classes = useDetailsPanelInfoStyles();
  const { t } = useTranslation(Namespaces.Admin);

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.column}>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.name')}</b>:<span>{rowData.name}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.company_name')}</b>:<span>{rowData.companyName}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.description')}</b>:
              <span>{rowData.description || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.email')}</b>:<span>{rowData.email || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.vat_number')}</b>:<span>{rowData.vatNumber || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.verified')}</b>:
              <span>
                {rowData.verified ? t('organizations.description_panel.yes') : t('organizations.description_panel.no')}
              </span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.details.language')}</b>:<span>{rowData.language}</span>
            </div>
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.line1')}</b>:<span>{rowData.address.line1}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.line2')}</b>:<span>{rowData.address.line2 || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.line3')}</b>:<span>{rowData.address.line3 || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.postal_code')}</b>:
              <span>{rowData.address.postalCode}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.city')}</b>:<span>{rowData.address.city}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.state')}</b>:<span>{rowData.address.state || '-'}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.country.code')}</b>:
              <span>{rowData.address.countryCode}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.country.name')}</b>:
              <span>{t(`countries:${rowData.address.countryCode}.name`)}</span>
            </div>
          </div>
          <div className={classes.linesContainer}>
            <div className={classes.line}>
              <b>{t('organizations.description_panel.address.country.calling_code')}</b>:
              <span>{t(`countries:${rowData.address.countryCode}.callingCode`)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
